import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { validateForm, forgotPasswordValidationRules } from "../../utils/formValidation";
import { forgotPassword } from "../../reduxStore/actions/userActionsHelper";
import { clearForgotPasswordState } from "../../reduxStore/reducers/userReducersHelper";
import { userForgotPasswordLoading$, userForgotPasswordSuccess$, userForgotPasswordError$ } from "../../reduxStore/selectors/userSelectors";
import styles from "./asset/forgotPasswordForm.module.scss";

const ForgotPasswordFormComponent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [email, setEmail] = useState("");
    const [errors, setErrors] = useState({});
    const isForgotPasswordLoading = useSelector(userForgotPasswordLoading$);
    const isForgotPasswordSuccess = useSelector(userForgotPasswordSuccess$);
    const forgotPasswordError = useSelector(userForgotPasswordError$);

    // Clear forgot password state when component unmounts
    useEffect(() => {
        return () => {
            dispatch(clearForgotPasswordState());
        };
    }, [dispatch]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const validation = validateForm({ email }, forgotPasswordValidationRules);
        setErrors(validation.errors);

        if (!validation.isValid) {
            return;
        }

        dispatch(forgotPassword.request(email));
    };

    const handleBackToSignIn = () => {
        navigate("/sign-in");
    };

    const renderContent = () => {
        if (isForgotPasswordSuccess) {
            return (
                <div className={styles.successContainer}>
                    <div className={styles.successIcon}>📧</div>
                    <div className={styles.successTitle}>Check Your Email</div>
                    <div className={styles.successMessage}>
                        We've sent a password reset link to:
                        <br />
                        <span className={styles.emailHighlight}>{email}</span>
                        <br /><br />
                        Click the link in the email to reset your password. If you don't see it, check your spam folder.
                    </div>
                    <button
                        className={styles.backButton}
                        onClick={handleBackToSignIn}
                    >
                        <div className={styles.backButtonText}>Back to Sign In</div>
                    </button>
                    <div className={styles.resendContainer}>
                        <span className={styles.resendText}>Didn't receive the email?</span>
                        <button
                            className={styles.resendButton}
                            onClick={() => {
                                dispatch(clearForgotPasswordState());
                                setEmail("");
                            }}
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <form className={styles.frameGroup} onSubmit={handleSubmit}>
                <div className={styles.frameWrapper}>
                    <div className={styles.inputParent}>
                        {forgotPasswordError && (
                            <div className={styles.errorContainer}>
                                <div className={styles.errorIcon}>⚠️</div>
                                <div className={styles.errorMessage}>
                                    {forgotPasswordError}
                                </div>
                            </div>
                        )}
                        <div className={styles.instructionText}>
                            Enter your email address and we'll send you a link to reset your password.
                        </div>
                        <div className={styles.input}>
                            <input
                                className={styles.yourEmail}
                                name="email"
                                type="email"
                                placeholder="Your Email Address"
                                value={email}
                                required
                                onChange={(e) => setEmail(e.target.value)}
                                style={{ borderColor: errors.email ? 'red' : '' }}
                                disabled={isForgotPasswordLoading}
                            />
                            <button
                                type="submit"
                                className={styles.resetButton}
                                disabled={isForgotPasswordLoading}
                            >
                                <div className={styles.resetButtonText}>
                                    {isForgotPasswordLoading ? (
                                        <>
                                            <span className={styles.loadingSpinner}></span>
                                            Send
                                        </>
                                    ) : (
                                        "Reset"
                                    )}
                                </div>
                            </button>
                        </div>
                        {errors.email && (
                            <div className={styles.errorMessage}>
                                {errors.email}
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.backToSignInWrapper}>
                    <span
                        className={styles.backToSignIn}
                        onClick={() => navigate('/sign-in')}
                    >
                        ← Back to Sign In
                    </span>
                </div>
            </form>
        );
    };

    return (
        <div className={styles.forgotPasswordForm}>
            <div className={styles.forgotPasswordChild}>
                <div className={styles.aceYourTestContainer}>
                    <p className={styles.aceYourTest}>Ace your test</p>
                    <p className={styles.aceYourTest}>with Prepal</p>
                </div>
                <img
                    className={styles.maskGroupIcon}
                    alt=""
                    src={require("../../publicAsset/media/book 1.svg").default}
                />
            </div>
            <div className={styles.prepalLogo3Parent}>
                <img
                    className={styles.prepalLogo3}
                    alt=""
                    src={require("../../publicAsset/media/Prepal Logo 3.svg").default}
                />
                <div className={styles.frameParent}>
                    <div className={styles.titleWrapper}>
                        <div className={styles.title}>
                            {isForgotPasswordSuccess ? "Email Sent!" : "Forgot Password?"}
                        </div>
                    </div>
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordFormComponent;